<template>
  <div>
    <CListGroup class="mb-3">
      <CListGroupItem class="d-flex align-items-center gap-2 cursor-pointer" @click="everything">
        <CIcon icon="cil-globe-alt" class="text-body-secondary" />
        <b class="flex-grow-1">All customers</b>
        <small class="text-body-secondary">every device you can reach</small>
      </CListGroupItem>
    </CListGroup>

    <CInputGroup class="mb-3">
      <CInputGroupText>
        <CIcon icon="cil-magnifying-glass" />
      </CInputGroupText>
      <CFormInput v-model="search" type="search" placeholder="Customer or short name"
        aria-label="Search customers" />
    </CInputGroup>

    <div v-if="loading" class="text-center text-body-secondary py-4">
      <CSpinner />
    </div>

    <CListGroup v-else-if="visible.length">
      <CListGroupItem v-for="item in visible" :key="item._key" class="d-flex align-items-center gap-2">
        <CBadge class="app-ping" shape="rounded-circle" :color="ping(item).color">
          <span class="visually-hidden">{{ ping(item).label }}</span>
        </CBadge>

        <button type="button" class="btn btn-link text-decoration-none text-start flex-grow-1 p-0"
          @click="select(item)">
          <b v-if="item.zabbix">({{ item.zabbix }})</b>
          <span class="ms-1">{{ item.name }}</span>
        </button>

        <CDropdown alignment="end">
          <CDropdownToggle color="light" size="sm" :aria-label="'Actions for ' + item.name" />
          <CDropdownMenu>
            <CDropdownItem class="cursor-pointer" @click="select(item)">
              <CIcon icon="cil-devices" />
              <small class="ms-2">Open devices</small>
            </CDropdownItem>
            <CDropdownItem class="cursor-pointer" @click="$emit('edit', item._key)">
              <CIcon icon="cil-pencil" />
              <small class="ms-2">Edit</small>
            </CDropdownItem>
            <CDropdownItem class="cursor-pointer" @click="send(item)">
              <CIcon icon="cil-send" />
              <small class="ms-2">Send configuration</small>
            </CDropdownItem>
            <CDropdownDivider />
            <CDropdownItem class="cursor-pointer" @click="destroy(item)">
              <CIcon icon="cil-trash" />
              <small class="ms-2">Delete</small>
            </CDropdownItem>
          </CDropdownMenu>
        </CDropdown>
      </CListGroupItem>
    </CListGroup>

    <CCallout v-else color="secondary">
      {{ search.trim().length ? 'No customer matches what you typed.' : 'No customer yet.' }}
    </CCallout>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import { list, remove, sendConfiguration } from '@/libraries/tenant'
import { ALL } from '@/libraries/route'
import { confirm, success } from '@/libraries/toast'
import type { define as TenantRecord } from '@/components/tenant/interfaces'
import type { Stored } from '@/types/api'

type Tenant = Stored<TenantRecord>

export default defineComponent({
  name: 'TenantList',
  emits: ['edit', 'select', 'changed'],
  setup() {
    return { router: useRouter() }
  },
  data() {
    return {
      all: [] as Tenant[],
      search: '',
      loading: false,
    }
  },
  computed: {
    visible(): Tenant[] {
      const needle = this.search.trim().toLowerCase()
      if (!needle) return this.all
      return this.all.filter((item) =>
        `${item.zabbix ?? ''} ${item.name}`.toLowerCase().includes(needle),
      )
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    ping(item: Tenant): { color: string; label: string } {
      if (item.linked?.garbage?.ping) return { color: 'success', label: 'Agent reporting' }
      return item.monitor === true
        ? { color: 'danger', label: 'Agent not reporting' }
        : { color: 'secondary', label: 'No agent installed' }
    },
    select(item: Tenant): void {
      this.router.push(`/tenant/${item._key}/device`)
      this.$emit('select')
    },
    everything(): void {
      this.router.push(`/tenant/${ALL}/device`)
      this.$emit('select')
    },
    send(item: Tenant): void {
      sendConfiguration(item._key, this.$el as HTMLElement)
    },
    destroy(item: Tenant): void {
      confirm('Yes, delete it!', this.$el as HTMLElement, () => {
        remove(item._key).then((removed) => {
          if (!removed) return
          success()
          this.load()
          this.$emit('changed')
        })
      })
    },
    async load(): Promise<void> {
      this.loading = true
      this.all = await list()
      this.loading = false
    },
  },
})
</script>

<style lang="scss">
.app-ping {
  width: 0.75rem;
  height: 0.75rem;
  padding: 0;
  flex: 0 0 auto;
}
</style>
